import { useState } from "react";
import { ProofDualPanels, ProofBars, ProofScoreboard, ProofScoreboardLight, ProofTablePlus } from "@/components/site/proof-options/ProofVariants";

const CONCEPTS = [
  {
    id: "score",
    label: "Option C · Scoreboard (Dark)",
    comp: ProofScoreboard,
    note: "The 2×3 versus scoreboard on the dark espresso theme — conventional value, a 'VS' divider, and the gold Intugle stat.",
  },
  {
    id: "score-light",
    label: "Option C · Scoreboard (Light)",
    comp: ProofScoreboardLight,
    note: "The same 2×3 versus scoreboard on the light cream theme — white cards, caramel accents and caramel stats.",
  },
  {
    id: "dual",
    label: "Option A · Head-to-Head Panels",
    comp: ProofDualPanels,
    note: "Two panels side by side: a dim 'Conventional Enterprise AI' column and a glowing 'With Intugle' column, aligned row-for-row so you scan straight across.",
  },
  {
    id: "bars",
    label: "Option B · Data-Viz Bars",
    comp: ProofBars,
    note: "Each metric shows two bars — a short muted conventional baseline vs a long gold Agentic Alpha bar — making the gap visual, with the animated number at the end.",
  },
  {
    id: "table",
    label: "Option D · Enhanced Table",
    comp: ProofTablePlus,
    note: "The current comparison table, refined: the 'With Intugle' column becomes a highlighted gold lane with clearer headers and a hover glow.",
  },
];

export default function ProofOptions() {
  const [active, setActive] = useState("score");
  const activeConcept = CONCEPTS.find((c) => c.id === active);
  const Comp = activeConcept.comp;

  return (
    <div className="min-h-screen bg-[#161009] text-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-10">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#EDA13A]">Section 3 — Comparison Layout Mockups</p>
        <h1 className="font-display text-2xl lg:text-4xl font-medium mt-2 tracking-tight">Pick a comparison layout for the Proof section</h1>
        <p className="text-sm text-white/55 mt-3 max-w-2xl leading-relaxed">
          Every option keeps the Conventional AI vs With Intugle comparison fully visible. Toggle between them, then tell me which to ship — no live changes until you approve.
        </p>

        <div className="flex flex-wrap gap-2 mt-6" data-testid="proof-concept-switcher">
          {CONCEPTS.map((c) => (
            <button
              key={c.id}
              data-testid={`proof-concept-btn-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`rounded-full px-4 py-2 text-[12px] font-semibold border transition-all duration-300 ${
                active === c.id
                  ? "bg-[#EDA13A] text-[#211307] border-[#EDA13A]"
                  : "bg-white/5 text-white/70 border-white/15 hover:border-white/40 hover:text-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <p className="text-[12px] text-white/45 mt-4 max-w-3xl leading-relaxed" data-testid="proof-option-note">
          {activeConcept.note}
        </p>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-14 pt-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/35 ml-2">Section preview</span>
        </div>
        <div className="rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)]" data-testid="proof-viewport-frame">
          <Comp />
        </div>
      </div>
    </div>
  );
}
